import {zodResolver} from '@hookform/resolvers/zod';
import {useMutation} from '@tanstack/react-query';
import {useForm} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';

import ErrorOutlined from '@/assets/icons/error-outlined.svg?react';
import {sendContactUsForm} from '@/entities/app/api/sendContactUsForm';
import {
  ContactFormValues,
  createContactSchema,
} from '@/entities/app/validations/contactUsForm';
import Button from '@/shared/components/button';
import FormField from '@/shared/components/form/FormField';
import DialogModal from '@/shared/components/modals/base/DialogModal';
import Textarea from '@/shared/components/TextArea';
import {useToggle} from '@/shared/hooks/useToggle';
import {localize} from '@/shared/services/i18n';

import ContactUsLabel from './components/ContactUsLabel';

const initialValues: ContactFormValues = {
  name: '',
  email: '',
  message: '',
};

const inputIds: Record<keyof ContactFormValues, string> = {
  name: 'contacts-name',
  email: 'contacts-email',
  message: 'contacts-message',
};

const ContactUsForm = () => {
  const successModal = useToggle();
  const errorModal = useToggle();
  const mutation = useMutation({
    mutationFn: sendContactUsForm,
    onSuccess: () => {
      successModal.show();
    },
    onError: (error) => {
      errorModal.show(error.message);
    },
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting},
  } = useForm<ContactFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(createContactSchema()),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
  });
  const disabled = isSubmitting || mutation.isPending;

  const onSubmit = handleSubmit(async (formValues) => {
    await mutation.mutateAsync(formValues);
    reset(initialValues);
  });

  return (
    <div className="mx-auto flex w-full max-w-[662px] flex-col gap-8 px-4">
      <div className="flex flex-col gap-2">
        <h4 className="font-semibold text-lg text-link sm:text-xl">
          {localize('contact_to_support')}
        </h4>
        <p className="text-black text-sm sm:text-base">
          {localize('contact_support_text')}
        </p>
      </div>
      <form onSubmit={onSubmit} className="mx-auto flex w-full flex-col gap-12">
        <div className="flex flex-col gap-5 sm:gap-8">
          <FormField
            label={localize('contact_us_name_label')}
            inputProps={{
              ...register('name'),
              disabled,
              placeholder: localize('contact_us_name_placeholder'),
            }}
            showValidationIcon={Boolean(errors.name)}
            error={errors.name?.message}
          />
          <FormField
            label={localize('contact_us_mail_label')}
            inputProps={{
              ...register('email'),
              disabled,
              placeholder: localize('contact_us_email_placeholder'),
            }}
            showValidationIcon={Boolean(errors.email)}
            error={errors.email?.message}
          />
          <div className="relative flex flex-col gap-1">
            <ContactUsLabel
              htmlFor={inputIds.message}
              label={localize('contact_us_message_label')}
            />
            <Textarea
              {...register('message')}
              id={inputIds.message}
              disabled={disabled}
              placeholder={localize('contact_us_message_placeholder')}
              rows={4}
              className={twMerge(
                'h-[124px] rounded-lg border border-inactive bg-white px-3.5 py-3 text-base text-black focus:border-link sm:text-xl',
                errors.message && 'border-error focus:border-error'
              )}
            />
            <p className="text-error text-xs empty:hidden">
              {errors.message?.message}
            </p>
          </div>
        </div>
        <Button
          type="submit"
          disabled={disabled}
          variant="primary"
          className="mx-auto max-w-[436px]"
        >
          {localize('main_contact_us')}
        </Button>
      </form>
      {successModal.open && (
        <DialogModal
          onClose={successModal.hide}
          okButton={{label: localize('ok'), onClick: successModal.hide}}
        >
          <p className="font-semibold text-lg">{localize('contact_success_text')}</p>
        </DialogModal>
      )}
      {errorModal.open && (
        <DialogModal
          onClose={errorModal.hide}
          okButton={{label: localize('ok'), onClick: errorModal.hide}}
        >
          <div className="flex flex-col items-center gap-1.5">
            <ErrorOutlined className="size-7 shrink-0" />
            <p className="font-semibold text-lg">{String(errorModal.value)}</p>
          </div>
        </DialogModal>
      )}
    </div>
  );
};

export default ContactUsForm;
